import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

function FlashDeliveryHomepage() {
  return (
    <>
      <Header />
      
      <section className="project-detail-section">
        <div className="container">
          <div className="project-detail-hero">
            <h1 className="project-detail-title">
              <span className="title-line-1">Flash Delivery –</span><br/>
              <span className="title-line-2">Homepage Redesign Case Study</span>
            </h1>
            <p className="project-detail-subtitle">Redesigning the homepage to improve clarity, speed, and conversion</p>
            <div className="project-meta">
              <span>UI/UX Designer</span>
              <span>•</span>
              <span>Figma</span>
              <span>•</span>
              <span>Website</span>
            </div>
          </div>

          <div className="project-images-single">
            <img src="/assets/flash.jpeg" alt="Flash Delivery Overview" />
          </div>

          <div className="project-detail-content">
            <section className="content-section">
              <h2>Project Overview</h2>
              <p>Flash Delivery is a high-speed quick-commerce platform delivering groceries, snacks, and daily essentials within minutes. In a market where speed is the primary value proposition, the digital storefront needs to be just as fast as the physical delivery.</p>
              
              <p>This project focused exclusively on the homepage. The goal was to move away from a cluttered, list-style layout and toward a structured, high-conversion portal that helps users find what they need and discover new deals effortlessly.</p>
            </section>

            <section className="content-section">
              <h2>The Problem (Before Redesign)</h2>
              <p>The original Flash Delivery homepage was struggling to communicate value. Key issues identified included a lack of visual hierarchy, where deals and promotions were buried under generic sections. The page felt like one long, undifferentiated scroll.</p>
              
              <p>Users were forced to work too hard to find categories or identify specific discounts, leading to higher bounce rates and missed opportunities for conversion. Essentially, the homepage wasn't guiding the user; it was just presenting them with a wall of products.</p>
              
              <p className="caption">The original UI lacked clear sectioning, making it difficult for users to distinguish between daily essentials and limited-time offers.</p>
              
              <div className="before-redesign-images">
                <img src="/assets/flash-delivery-third.jpeg" alt="Flash Delivery Before 1" />
                <img src="/assets/flash-delivery-fourth.png" alt="Flash Delivery Before 2" />
              </div>
            </section>

            <section className="content-section">
              <h2>Redesign Goal</h2>
              <p>The objective was a complete structural overhaul of the homepage. Rather than redesigning every individual element, I focused on how those elements are organized to create a better flow.</p>
              
              <h3>Key goals included:</h3>
              <ul>
                <li>Immediate value: Ensure users understand the service and current deals within the first 3 seconds.</li>
                <li>Improved Discovery: Make category browsing more intuitive and visual.</li>
                <li>Decision Speed: Reduce physical and cognitive scrolling by grouping related products effectively.</li>
                <li>Conversion Focus: Strategically place CTAs and clear pricing to move users into the funnel faster.</li>
              </ul>
            </section>

            <section className="content-section">
              <h2>Design Thinking</h2>
              <p>When people open a delivery app, they are usually in a 'task-oriented' mindset—they want to find an item and check out as quickly as possible. I approached the redesign by treating the homepage as a funnel.</p>
              
              <p>The top of the page serves the broader 'intent' (search and primary deals), while the middle sections handle 'discovery' (categories and curated lists). By creating a clear vertical rhythm, I wanted to guide the user's eye naturally from one value proposition to the next, reducing the amount of time they spent thinking about where to click next.</p>
            </section>

            <section className="content-section">
              <h2>Homepage Structure (After Redesign)</h2>
              <p>The new layout is designed to be scanned, not just read. The structure follows a logical progression:</p>
              
              <ul>
                <li>Hero Section: High-impact visuals stating the current primary offer (e.g., Free Delivery) with a clear CTA.</li>
                <li>Deal Hub: Dedicated rows for 'Flash Deals' and 'Price Drops' to create a sense of urgency.</li>
                <li>Visual Categories: Easy-to-tap icons for quick access to Groceries, Drinks, or Snacks.</li>
                <li>Product Rows: Dynamic listings based on user popularity and seasonal items.</li>
                <li>Utility & Trust: A cleaned-up footer and app download prompt to close the loop.</li>
              </ul>
            </section>

            <section className="content-section">
              <h2>Key UX Improvements (Before vs After)</h2>
              <p>The most significant change was the move from 'clutter' to 'clarity.' By instituting a consistent card-based system, I gave each product and deal its own space. I increased the vertical white space between sections to prevent the 'visual overload' that was present in the old design.</p>
              
              <p>This allows for faster scanning—a user can now identify a category or a discount without having to stop and stare at the screen.</p>
              
              <div className="ux-comparison-images">
                <img src="/assets/flash-delivery-fifth.png" alt="UX Improvement 1" />
                <img src="/assets/flash-delivery-sixth.png" alt="UX Improvement 2" />
              </div>
            </section>

            <section className="content-section">
              <h2>Visual Design Direction</h2>
              <p>The visual identity for Flash Delivery is about energy and efficiency. I used high-contrast colors (like bright yellows or reds) purposefully—only for deals and primary CTAs—to catch the user's eye.</p>
              
              <p>Typography was kept clean and bold to ensure readability on mobile devices while people are on the go. The use of rounded corners for item cards makes the platform feel approachable and modern, aligning with the brand's 'friendly assistant' persona.</p>
            </section>

            <section className="content-section">
              <h2>Final Outcome</h2>
              <p>The redesigned homepage feels significantly more responsive to user intent. By prioritizing structure over sheer volume of content, the interface now feels faster and more trustworthy.</p>
              
              <p>The clear separation of deals and categories ensures that users are never more than two taps away from what they need. This results in a cleaner first impression and a much more polished, professional look for the brand.</p>
            </section>

            <section className="content-section">
              <h2>Prototype (Figma)</h2>
              
              <div className="project-links">
                <a href="https://www.figma.com/design/p98WuwNExmOeb4GMa191CC/flash-Website---ansh-chopra?node-id=0-1&t=uAxaBgqDBLbmgv7U-1" target="_blank" rel="noopener noreferrer" className="btn">
                  View Designs <span className="btn-arrow">→</span>
                </a>
                <a href="https://www.figma.com/proto/p98WuwNExmOeb4GMa191CC/flash-Website---ansh-chopra?node-id=7-520&p=f&t=C83wBIp26Lodes79-1&scaling=min-zoom&content-scaling=fixed&page-id=0%3A1" target="_blank" rel="noopener noreferrer" className="btn">
                  View Prototype <span className="btn-arrow">→</span>
                </a>
              </div>
            </section>

            <section className="content-section">
              <h2>Learnings & Reflection</h2>
              <p>This project was a great reminder that great UX is often about what you choose to hide or deprioritize. The 'more is more' approach rarely works for a quick-commerce homepage.</p>
              
              <p>I learned how much the hierarchy of a single page can influence the entire user journey. Moving forward, I'll continue to focus on reducing cognitive load as a primary metric for success, especially in industries where speed and convenience are the main reasons users choose a service.</p>
              
              <div className="final-ui-image">
                <img src="/assets/flash-delivery-seventh.png" alt="Final Reflection" />
              </div>
            </section>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default FlashDeliveryHomepage;
